import { useEffect, useState } from 'react';
import { logger } from '../../../../../commons/utils/logger';
import { putRequestWithRetry } from '../../../../../commons/utils/asyncRequest';
import {
  PopoverMultipleLines,
  PopoverWithContent,
  VALUE_STYLE,
} from '../types';
import { useAttributionContext } from './AttributionContext';

interface ActivationData {
  id: string;
  attachedComponent: string;
  toggled: boolean;
}

export interface ActivationResponse {
  displayText?: string;
  popoverContent?: PopoverWithContent | PopoverMultipleLines | string;
  status?: VALUE_STYLE;
  error?: string;
}

interface PutActivationDataInput {
  activationAPI: string;
  isActive: boolean;
  id: string;
  attachedComponent: string;
  apiCallFailureMessage: string;
  disabled: boolean;
  blockActivationEventName?: string;
}

export interface UseActivationDataResponse {
  toggled: boolean;
  showFailBanner: boolean;
  requestFailMessage: string | null;
  bannerCloseEvent: () => void;
  activationToggleEvent: () => void;
  isDisabled: boolean;
}

export const ajaxPutActivationInfo = async (
  url: string,
  data: ActivationData,
  timeout?: number,
  headers?: Record<string, string>,
): Promise<ActivationResponse | null> => {
  try {
    return await putRequestWithRetry<ActivationResponse>({
      json: data,
      url,
      timeout,
      headers,
    });
  } catch (error: any) {
    logger.info(
      `asyncRequests - activation put request for endpoint ${url}. error: ${error}`,
    );
    return null;
  }
};

export const useActivationData = ({
  activationAPI,
  isActive,
  id,
  attachedComponent,
  apiCallFailureMessage,
  disabled,
  blockActivationEventName,
}: PutActivationDataInput): UseActivationDataResponse => {
  const [toggled, setToggled] = useState(isActive);
  const [isDisabled, setIsDisabled] = useState(disabled);
  const [showFailBanner, setShowFailBanner] = useState(true);
  const [requestFailMessage, setRequestFailMessage] = useState<string | null>(
    null,
  );
  const { setActivationToggleData } = useAttributionContext();
  useEffect(() => {
    setToggled(isActive);
  }, [isActive]);

  useEffect(() => setIsDisabled(disabled), [disabled]);

  const activationToggleEvent = async () => {
    if (blockActivationEventName) {
      dispatchEvent(new CustomEvent(blockActivationEventName));
      return;
    }
    if (isDisabled) {
      return;
    }
    const initialToggled = toggled;
    setIsDisabled(true);
    setToggled(!toggled);
    const activationResponse = await ajaxPutActivationInfo(
      activationAPI,
      { id, attachedComponent, toggled: !toggled },
      undefined,
      {
        'Content-type': 'application/json; charset=UTF-8',
      },
    );
    setIsDisabled(false);
    if (!activationResponse || activationResponse.error) {
      setRequestFailMessage(activationResponse?.error ?? apiCallFailureMessage);
      setShowFailBanner(true);
      setToggled(initialToggled);
      return;
    }
    setActivationToggleData({
      attachedComponent: attachedComponent,
      activationResponse: activationResponse,
    });
  };

  return {
    toggled,
    showFailBanner,
    requestFailMessage,
    bannerCloseEvent: () => setShowFailBanner(false),
    activationToggleEvent,
    isDisabled,
  };
};
